import { childToProjectDataUtils, IProject, ProjectContext } from "@droid/project-v2-interface";
import { Box, Button, Grid2 } from "@mui/material";
import { useRouter } from "next/router";
import { PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { projectUtils } from "../../utils/projectUtils";
import { runProject } from "../../utils/runProject";
import { Log } from "@fanfanlo";
import { DeleteProjectButton } from "../components/DeleteProjectButton";
import { autoWebViewJs } from "@droid/android";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SettingsIcon from '@mui/icons-material/Settings';

const fileLog = new Log(false, 'ProjectUI_f')
// fileLog.pause = true;  // 注释掉：允许日志输出
// fileLog.childrenPaused = true;  // 注释掉：允许子日志输出
function ProjectName({ project }: PropsWithChildren & { project: IProject }) {
    const router = useRouter()
    const { t } = useTranslation('droid-project/project/ui/project/content')

    const [isRunning, setIsRunning] = useState(false)

    useEffect(() => {
        const fnLog = fileLog.sub(false, 'ProjectName_useEffect_fn')
        fnLog.pause = true;  // 注释掉：允许日志输出
        fnLog.log('[ProjectName] useEffect 初始化')
        fnLog.log('[ProjectName] project.ulid:', project.ulid)

        const projectUlid = project.ulid
        const hasProjectUlid = projectUlid ? true : false
        fnLog.log('[ProjectName] 是否有projectUlid:', hasProjectUlid)

        if (!hasProjectUlid) {
            fnLog.log('[ProjectName] projectUlid为空,跳过初始化')
            return
        }

        try {
            fnLog.log('[ProjectName] 调用 RhinoManager.isRunning 检查初始状态')

            const script = `
            com.fanfanlo.droidlib.auto.rhino.RhinoManager.isRunning("${projectUlid}")
            `
            const {javaResultString} = autoWebViewJs.callScript(script)
            fnLog.log('[ProjectName] RhinoManager.isRunning 返回:', javaResultString)
            const isRunning = javaResultString?.toString() == 'true';

            fnLog.log('[ProjectName] 初始运行状态:', isRunning)
            setIsRunning(isRunning)
        } catch (error) {
            fnLog.log('[ProjectName] 调用 isRunning 异常:', error)
        }


        const eventNameOn = `project_${projectUlid}_running_on`
        const eventNameOff = `project_${projectUlid}_running_off`
        fnLog.log('[ProjectName] 监听on事件:', eventNameOn)
        fnLog.log('[ProjectName] 监听off事件:', eventNameOff)

        const handleRunningOn = (event: any) => {
            fnLog.log('[ProjectName] 收到running_on事件')
            fnLog.log('[ProjectName] event:', event)
            fnLog.log('[ProjectName] 更新运行状态为: true')
            setIsRunning(true)
        }

        const handleRunningOff = (event: any) => {
            fnLog.log('[ProjectName] 收到running_off事件')
            fnLog.log('[ProjectName] event:', event)
            fnLog.log('[ProjectName] 更新运行状态为: false')
            setIsRunning(false)
        }

        fnLog.log('[ProjectName] 注册on事件监听器')
        window.addEventListener(eventNameOn, handleRunningOn)
        fnLog.log('[ProjectName] 注册off事件监听器')
        window.addEventListener(eventNameOff, handleRunningOff)

        return () => {
            fnLog.log('[ProjectName] useEffect 清理')
            fnLog.log('[ProjectName] 移除on事件监听器:', eventNameOn)
            window.removeEventListener(eventNameOn, handleRunningOn)
            fnLog.log('[ProjectName] 移除off事件监听器:', eventNameOff)
            window.removeEventListener(eventNameOff, handleRunningOff)
        }
    }, [project.ulid])

    async function onRunClick() {
        const fnLog = fileLog.sub(false, 'onRunClick_fn')
        // fnLog.pause = true;  // 注释掉：允许日志输出
        fnLog.log('[onRunClick] 开始运行项目')
        fnLog.log('[onRunClick] project.ulid:', project.ulid)
        fnLog.log('[onRunClick] project.name:', project.name)

        try {
            const result = await runProject(project)
            fnLog.log('[onRunClick] runProject 返回结果:', result)
        } catch (error) {
            fnLog.log('[onRunClick] runProject 执行异常:', error)
        }
    }

    async function onStopClick() {
        const fnLog = fileLog.sub(false, 'onStopClick_fn')
        // fnLog.pause = true;  // 注释掉：允许日志输出
        fnLog.log('[onStopClick] 开始停止项目')
        fnLog.log('[onStopClick] project.ulid:', project.ulid)

        const projectUlid = project.ulid
        const hasProjectUlid = projectUlid ? true : false
        fnLog.log('[onStopClick] 是否有projectUlid:', hasProjectUlid)

        if (!hasProjectUlid) {
            fnLog.log('[onStopClick] projectUlid为空,无法停止')
            return
        }

        try {
            const script = `
            com.fanfanlo.droidlib.auto.rhino.JsRhinoManager.jsCallStopById("${projectUlid}")
            `
            fnLog.log('[onStopClick] 调用stopById脚本', script)
            const {javaResultString} = autoWebViewJs.callScript(script)
            fnLog.log('[onStopClick] RhinoManager.stopById 返回结果:', javaResultString)
        } catch (error) {
            fnLog.log('[onStopClick] stopById 执行异常:', error)
        }
    }

    async function onToggleRunClick() {
        const fnLog = fileLog.sub(false, 'onToggleRunClick_fn')
        // fnLog.pause = true;  // 注释掉：允许日志输出
        fnLog.log('[onToggleRunClick] 切换运行状态')
        fnLog.log('[onToggleRunClick] 当前isRunning:', isRunning)

        const currentIsRunning = isRunning
        fnLog.log('[onToggleRunClick] currentIsRunning值:', currentIsRunning)

        if (currentIsRunning) {
            fnLog.log('[onToggleRunClick] 当前正在运行,准备停止')
            await onStopClick()
        } else {
            fnLog.log('[onToggleRunClick] 当前未运行,准备启动')
            await onRunClick()
        }
    }

    function edit() {
        const path = `/project-editor?ulid=${project.ulid}`
        router.push(path, undefined, {shallow: true})
    }

    function openSettings() {
        const fnLog = fileLog.sub(false, 'openSettings_fn')
        // fnLog.pause = true;  // 注释掉：允许日志输出
        fnLog.log('[openSettings] 打开设置页面')
        fnLog.log('[openSettings] project.ulid:', project.ulid)

        const currentPath = router.asPath
        fnLog.log('[openSettings] 当前路径:', currentPath)

        const settingsPath = `/project-settings?projectUlid=${project.ulid}&backPath=${encodeURIComponent(currentPath)}`
        fnLog.log('[openSettings] 跳转到:', settingsPath)

        router.push(settingsPath, undefined, {shallow: true})
    }
    return (
        <Grid2 container spacing={2} sx={{ width: "100%" }}
        >
            <Grid2 size={"grow"}>
                <span >{projectUtils.getProjectName(project)}</span>
            </Grid2>
            <Grid2 container spacing={2}>
                <Button
                    data-testid="ProjectUI__toggleRunButton__01JFXYZY5N9O0P1Q2R3S4T5U6"
                    {...childToProjectDataUtils.getProjectElementsId({ project }, isRunning ? 'project-list-stop-btn' : 'project-list-run-btn')}
                    data-listof-project-list-btn-id={project.ulid}
                    id="project-simple-title-list-toggle-btn"
                    data-id="project-simple-title-list-toggle-btn"
                    onClick={onToggleRunClick}
                    color={isRunning ? "error" : "primary"}
                    startIcon={isRunning ? <StopIcon /> : <PlayArrowIcon />}
                >
                    {isRunning ? t('content.project.stop') : t('content.project.run')}
                </Button>
                <Button
                    data-testid="ProjectUI__editButton__01JFXYZZ6O0P1Q2R3S4T5U6V7"
                    {...childToProjectDataUtils.getProjectElementsId({ project }, 'project-list-edit-btn')}

                    data-listof-project-list-edit-btn-id={project.ulid}
                    id="project-simple-title-list-edit-btn"
                    data-id="project-simple-title-list-edit-btn"
                    onClick={edit}>{t('content.project.edit')}</Button>
               
                <DeleteProjectButton />
            </Grid2>
        </Grid2>
    )
}

export function ProjectUI({ project }: PropsWithChildren & { project: IProject }) {
    return (
        <ProjectContext.Provider value={project}>
            <Box sx={{ width: "100%", backgroundColor: "#f0f0f0", padding: 1 }}
                data-ulid={project.ulid}
                {...childToProjectDataUtils.getProjectElementsId({ project })}>
                <ProjectName project={project} />
            </Box>
        </ProjectContext.Provider>
    )
}